import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Search, Users, MapPin, Plus, Crown, Clock, Check, Navigation, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import CreateClubModal from './CreateClubModal';

// Haversine formula to calculate distance between two points
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Distance in miles
};

export default function ClubsList({ user }) {
  const navigate = useNavigate();
  const [clubs, setClubs] = useState([]);
  const [myClub, setMyClub] = useState(null);
  const [myMemberships, setMyMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nameSearch, setNameSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Location search state
  const [locationQuery, setLocationQuery] = useState('');
  const [locationResults, setLocationResults] = useState([]);
  const [searchingLocation, setSearchingLocation] = useState(false);
  const [filterCoordinates, setFilterCoordinates] = useState(null);
  const [filterLocationName, setFilterLocationName] = useState('');
  const [filterRadius, setFilterRadius] = useState([15]); // 15km default
  const [useGPS, setUseGPS] = useState(false);
  const [loadingGPS, setLoadingGPS] = useState(false);
  const searchTimeoutRef = useRef(null);
  const isMetric = user?.unit_preference === 'metric';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      console.log('[ClubsList] Fetching clubs data...');
      
      // Fetch clubs list - this is the main one
      let clubsData = [];
      try {
        const clubsRes = await axios.get('/clubs');
        clubsData = clubsRes.data || [];
        console.log('[ClubsList] Clubs fetched:', clubsData.length);
      } catch (clubsErr) {
        console.error('[ClubsList] Failed to fetch clubs:', clubsErr.response?.status);
        // Continue with empty clubs - don't break the whole page
      }
      
      // Fetch my club (optional - user might not have one)
      let myClubData = null;
      try {
        const myClubRes = await axios.get('/clubs/my-club');
        myClubData = myClubRes.data;
      } catch (e) {
        // Expected if user doesn't have a club
      }
      
      // Fetch memberships (optional)
      let membershipsData = [];
      try {
        const membershipsRes = await axios.get('/clubs/my-memberships');
        membershipsData = membershipsRes.data || [];
      } catch (e) {
        // Expected if user isn't in any clubs
      }
      
      setClubs(clubsData);
      setMyClub(myClubData);
      setMyMemberships(membershipsData);
    } catch (error) {
      console.error('[ClubsList] Unexpected error:', error);
      setClubs([]);
      setMyClub(null);
      setMyMemberships([]);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinClub = async (clubId, e) => {
    e.stopPropagation();
    try {
      await axios.post(`/clubs/${clubId}/join`);
      toast.success('Join request sent! Waiting for admin approval.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join club');
    }
  };

  // Location search handlers
  const handleLocationSearch = (query) => {
    setLocationQuery(query);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (query.length < 3) {
      setLocationResults([]);
      return;
    }
    
    searchTimeoutRef.current = setTimeout(async () => {
      setSearchingLocation(true);
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await response.json();
        setLocationResults(data);
      } catch (error) {
        console.error('Search failed:', error);
        setLocationResults([]);
      } finally {
        setSearchingLocation(false);
      }
    }, 300);
  };

  const handleLocationResultClick = (result) => {
    const coords = {
      lat: parseFloat(result.lat),
      lon: parseFloat(result.lon)
    };
    const name = result.display_name.split(',')[0];
    
    setFilterCoordinates(coords);
    setFilterLocationName(name);
    setLocationQuery(name);
    setLocationResults([]);
    setUseGPS(false);
  };

  const requestGPSLocation = () => {
    if ('geolocation' in navigator) {
      setLoadingGPS(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const coords = {
            lat: position.coords.latitude,
            lon: position.coords.longitude
          };
          setFilterCoordinates(coords);
          setUseGPS(true);
          
          // Reverse geocode
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${coords.lat}&lon=${coords.lon}&zoom=10`,
              { headers: { 'Accept-Language': 'en' } }
            );
            const data = await response.json();
            const name = data.address?.city || data.address?.town || 'Your Location';
            setFilterLocationName(name);
          } catch {
            setFilterLocationName('Your Location');
          }
          setLoadingGPS(false);
        },
        () => {
          toast.error('Could not get your location');
          setLoadingGPS(false);
        },
        { timeout: 10000 }
      );
    }
  };

  const clearLocationFilter = () => {
    setFilterCoordinates(null);
    setFilterLocationName('');
    setLocationQuery('');
    setUseGPS(false);
  };

  // Filter clubs by name and location
  const filteredClubs = (clubs || []).filter(club => {
    if (!club || !club.name) return false;
    
    // Name filter
    const matchesName = club.name.toLowerCase().includes(nameSearch.toLowerCase()) ||
      (club.description || '').toLowerCase().includes(nameSearch.toLowerCase());
    
    if (!matchesName) return false;
    
    // Location filter
    if (filterCoordinates && club.latitude && club.longitude) {
      const distMiles = calculateDistance(
        filterCoordinates.lat, filterCoordinates.lon,
        club.latitude, club.longitude
      );
      const radiusMiles = isMetric ? filterRadius[0] * 0.621371 : filterRadius[0];
      return distMiles <= radiusMiles;
    }
    
    return true;
  }).sort((a, b) => {
    // Sort by distance if location filter is active
    if (filterCoordinates) {
      const distA = (a.latitude && a.longitude) ? calculateDistance(filterCoordinates.lat, filterCoordinates.lon, a.latitude, a.longitude) : Infinity;
      const distB = (b.latitude && b.longitude) ? calculateDistance(filterCoordinates.lat, filterCoordinates.lon, b.latitude, b.longitude) : Infinity;
      return distA - distB;
    }
    return 0;
  });

  const getMembershipStatus = (club) => {
    if (club.is_admin) return 'admin';
    if (club.is_member) return 'member';
    if (club.membership_status === 'pending') return 'pending';
    return null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <p className="text-muted-foreground">Loading clubs...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="clubs-list">
      {/* Search Filters */}
      <Card className="p-4">
        <div className="space-y-4">
          {/* Club Name Search */}
          <div>
            <Label>Search by Club Name</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search club"
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                className="pl-10"
                data-testid="club-name-search"
              />
            </div>
          </div>

          {/* Location Search */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="relative">
              <Label>{useGPS ? 'Using GPS Location' : 'Search by Location'}</Label>
              {useGPS ? (
                <div className="flex items-center gap-2 p-2 bg-green-500/20 border border-green-500/30 rounded-md mt-1">
                  <Navigation className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-500 truncate flex-1">{filterLocationName}</span>
                  <Button variant="ghost" size="sm" onClick={clearLocationFilter}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={locationQuery}
                    onChange={(e) => handleLocationSearch(e.target.value)}
                    placeholder="Search location"
                    className="pl-10"
                    data-testid="club-location-search"
                  />
                  {searchingLocation && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin" />
                  )}
                  
                  {locationResults.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {locationResults.map((result, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleLocationResultClick(result)}
                          className="w-full px-4 py-2 text-left hover:bg-accent border-b border-border last:border-b-0"
                        >
                          <p className="text-sm text-foreground truncate">{result.display_name}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Radius + GPS Button */}
            <div>
              <Label>Distance: {filterRadius[0]} {isMetric ? 'km' : 'miles'}</Label>
              <div className="flex items-center gap-2 mt-1">
                <Slider
                  value={filterRadius}
                  onValueChange={setFilterRadius}
                  min={1}
                  max={50}
                  step={1}
                  className="flex-1"
                  disabled={!filterCoordinates}
                />
                {!useGPS && (
                  <Button variant="outline" size="sm" onClick={requestGPSLocation} disabled={loadingGPS}>
                    {loadingGPS ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Active filter indicator */}
          {filterCoordinates && (
            <div className="flex items-center gap-2 text-sm bg-purple-500/10 border border-purple-500/20 rounded-md p-2">
              <MapPin className="w-4 h-4 text-purple-500" />
              <span>Showing clubs within <strong>{filterRadius[0]} {isMetric ? 'km' : 'miles'}</strong> of <strong>{filterLocationName}</strong></span>
              <Button variant="ghost" size="sm" onClick={clearLocationFilter} className="ml-auto">
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Create Club Button */}
      {!myClub && (
        <div className="flex justify-end">
          <Button onClick={() => setShowCreateModal(true)} data-testid="create-club-button">
            <Plus className="w-4 h-4 mr-2" />
            Create Club
          </Button>
        </div>
      )}

      {/* My Club Section */}
      {myClub && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">My Club</h3>
          <Card 
            className="cursor-pointer hover:border-purple-500/50 transition-colors border-purple-500/30 bg-purple-500/5"
            onClick={() => navigate(`/club/${myClub.id}`)}
            data-testid="my-club-card"
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-lg bg-purple-600 flex items-center justify-center flex-shrink-0">
                  {myClub.logo ? (
                    <img src={myClub.logo} alt={myClub.name} className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <span className="text-2xl font-bold text-white">{myClub.name.charAt(0)}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-lg text-foreground truncate">{myClub.name}</h3>
                    <Badge className="bg-purple-600"><Crown className="w-3 h-3 mr-1" />Admin</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{myClub.description}</p>
                  <div className="flex items-center gap-4 mt-2">
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Users className="w-3 h-3" /> {myClub.member_count} members
                    </span>
                    {myClub.formatted_address && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {myClub.formatted_address.split(',')[0]}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* My Memberships */}
      {(myMemberships || []).length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            Clubs I'm In ({myMemberships.length})
          </h3>
          <div className="grid gap-3">
            {myMemberships.map((club) => (
              <Card 
                key={club.id}
                className="cursor-pointer hover:border-purple-500/50 transition-colors"
                onClick={() => navigate(`/club/${club.id}`)}
                data-testid={`membership-card-${club.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0">
                      {club.logo ? (
                        <img src={club.logo} alt={club.name} className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        <span className="text-lg font-bold text-white">{club.name.charAt(0)}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate">{club.name}</h3>
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Users className="w-3 h-3" /> {club.member_count} members
                      </span>
                    </div>
                    <Badge variant="outline" className="text-green-500 border-green-500/30">
                      <Check className="w-3 h-3 mr-1" />Member
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Discover Clubs */}
      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Discover Clubs ({filteredClubs.filter(c => !c.is_member && !c.is_admin).length})
        </h3>
        {filteredClubs.filter(c => !c.is_member && !c.is_admin).length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">
                {nameSearch ? 'No clubs match your search' : 'No clubs to discover yet'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {filteredClubs.filter(c => !c.is_member && !c.is_admin).map((club) => {
              const status = getMembershipStatus(club);
              return (
                <Card 
                  key={club.id}
                  className="cursor-pointer hover:border-purple-500/50 transition-colors"
                  onClick={() => navigate(`/club/${club.id}`)}
                  data-testid={`club-card-${club.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0">
                        {club.logo ? (
                          <img src={club.logo} alt={club.name} className="w-full h-full object-cover rounded-lg" />
                        ) : (
                          <span className="text-xl font-bold text-white">{club.name.charAt(0)}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground truncate">{club.name}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{club.description}</p>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Users className="w-3 h-3" /> {club.member_count} members
                          </span>
                          {club.formatted_address && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <MapPin className="w-3 h-3" /> {club.formatted_address.split(',')[0]}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex-shrink-0">
                        {status === 'pending' ? (
                          <Badge variant="outline" className="text-yellow-500 border-yellow-500/30">
                            <Clock className="w-3 h-3 mr-1" />Pending
                          </Badge>
                        ) : (
                          <Button 
                            size="sm" 
                            onClick={(e) => handleJoinClub(club.id, e)}
                            data-testid={`join-club-${club.id}`}
                          >
                            Join
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Club Modal */}
      {showCreateModal && (
        <CreateClubModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchData();
          }}
        />
      )}
    </div>
  );
}
